import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ReferenceLine, Tooltip,
} from 'recharts';
import styles from './QQTimeOnTask.module.css';
import common from '../../../../../../styles/common-styles.module.css';
import {
  WIDGET_TYPE_TIME_SCORE_CHART,
} from '../../../../constants';
import { generateChartDescription, getChartAltText } from '../../../../utils';

const LABEL_MARGIN = 100;

const renderScoreLabel = (box, value, type) => {
  const suffix = type === WIDGET_TYPE_TIME_SCORE_CHART ? '%' : '';
  return (
    <text
      fontSize="14"
      fontFamily="Source Sans Pro"
      textAnchor="middle"
      y={box.viewBox.y}
    >
      <tspan
        className={styles.label}
        x={box.viewBox.width + LABEL_MARGIN}
      >
        {`${value}${suffix}`}
      </tspan>
      <tspan
        className={styles.labelSmall}
        x={box.viewBox.width + (LABEL_MARGIN - 2)}
        dy="12"
      >
        avg
      </tspan>
    </text>
  );
};

const renderCustomLabel = (box, value) => (
  <text
    fontSize="14"
    fontFamily="Source Sans Pro"
    textAnchor="middle"
    x={box.viewBox.x}
    y={45}
  >
    <tspan
      className={styles.label}
      x={box.viewBox.x}
    >
      {`${value} min `}
    </tspan>
    <tspan className={styles.labelSmall}>avg</tspan>
  </text>
);

const formatValue = (value, decimalPlaces = 1) => {
  if (value) return +value.toFixed(decimalPlaces);
  return 0;
};

const CustomTooltip = ({ payload }) => {
  const payloadData = get(payload, '[0].payload');
  if (payloadData) {
    return (
      <div className={styles.customTooltip} >
        <div className={styles.customTooltipLegend}>
          <b>{payloadData.name}</b>
          <br /><span>Attempts: {payloadData.attempts}</span>
          <br /><span>Minutes: {formatValue(payloadData.time, 2)}</span>
          <br /><span>Score: {`${formatValue(payloadData.grade)}%`}</span>
        </div>
      </div>
    );
  }
  return null;
};

CustomTooltip.propTypes = {
  payload: PropTypes.array,
};

function QQTimeOnTaskChart({ data, type }) {
  const formattedAverageTime = formatValue(data.averageTime);
  const formattedAverageScore = formatValue(data.averageScore);
  const formattedAverageAttempts = formatValue(data.averageAttempts);
  const dataKey = type === WIDGET_TYPE_TIME_SCORE_CHART ? 'grade' : 'attempts';
  const YAxisName = type === WIDGET_TYPE_TIME_SCORE_CHART ? 'Activity Score (%)' : 'Total Attempts';
  const averageProp = type === WIDGET_TYPE_TIME_SCORE_CHART ? 'averageScore' : 'averageAttempts';
  const formattedAvg = type === WIDGET_TYPE_TIME_SCORE_CHART ? formattedAverageScore : formattedAverageAttempts;
  
  const chartDescription = generateChartDescription(data, type);
  const chartAltText = getChartAltText(data, type);
  const chartDescriptionId = `chart-description-${type}`;
  
  return (
    <div>
      <ResponsiveContainer height={250}>
        <ScatterChart
          margin={{ top: 50, right: 50, left: 10, bottom: 20 }}
          role="img"
          aria-label={chartAltText}
          aria-describedby={chartDescriptionId}
        >
        <XAxis
          type="number"
          dataKey="time"
          name="Minutes"
          label={{
            value: 'Minutes',
            position: 'bottom',
            fontSize: 16,
            fontFamily: 'Source Sans Pro',
            fontWeight: 600,
            dy: -5,
            fill: '#535353',
          }}
        />
        <YAxis
          type="number"
          dataKey={dataKey}
          name={YAxisName}
          label={{
            value: YAxisName,
            position: 'insideLeft',
            angle: -90,
            dx: 10,
            dy: 60,
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'Source Sans Pro',
            fill: '#535353',
          }}
        />
        <Scatter data={data.insightsData} fill="var(--prs-c-info-800)" />
        <ReferenceLine
          stroke="#535353"
          strokeDasharray="3 3"
          x={data.averageTime}
          label={{
            content: box => renderCustomLabel(box, formattedAverageTime),
            position: 'top',
          }}
        />
        <ReferenceLine
          stroke="#535353"
          strokeDasharray="3 3"
          y={data[averageProp]}
          label={{
            content: box => renderScoreLabel(box, formattedAvg, type),
            position: 'right',
          }}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />
      </ScatterChart>
    </ResponsiveContainer>
    <div id={chartDescriptionId} className={common.screenReaderText}>
      {chartDescription}
    </div>
  </div>
  );
}

QQTimeOnTaskChart.propTypes = {
  data: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
};

export default QQTimeOnTaskChart;

